
import React, { useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusCircle, MoreHorizontal, Calendar, Trash, Plus, Edit, BarChart2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { EmptyStateAnalytics } from "@/components/analytics/EmptyStateAnalytics";
import { useTheme } from "@/contexts/ThemeContext";
import DeliverableForm from './DeliverableForm';
import { TwitterCampaign } from '@/types/twitterTypes';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface TwitterCampaignListProps {
  onCampaignSelect: (campaignId: string) => void;
}

export const TwitterCampaignList: React.FC<TwitterCampaignListProps> = ({ onCampaignSelect }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [currentCampaign, setCurrentCampaign] = useState<TwitterCampaign | null>(null);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [newCampaignDescription, setNewCampaignDescription] = useState('');
  const [refreshingMetrics, setRefreshingMetrics] = useState<string | null>(null);
  
  // Fetch campaigns
  const { data: campaigns = [], isLoading, error } = useQuery({
    queryKey: ['twitter-campaigns'],
    queryFn: async () => {
      try {
        // Get all campaigns
        const { data: campaignsData, error: campaignsError } = await supabase
          .from('twitter_campaigns')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (campaignsError) throw campaignsError;
        
        return campaignsData as TwitterCampaign[];
      } catch (error: any) {
        throw error;
      }
    }
  });
  
  // Refresh metrics for a campaign
  const handleRefreshMetrics = async (campaignId: string) => {
    try {
      setRefreshingMetrics(campaignId);
      
      // Direct call to the refresh-campaign-metrics edge function
      const response = await supabase.functions.invoke('refresh-campaign-metrics', {
        body: { campaignId }
      });
      
      if (response.error) {
        console.error("Error response from refresh-campaign-metrics:", response.error);
        throw new Error(response.error.message || 'Failed to refresh metrics');
      }
      
      if (!response.data.success) {
        console.error("Failed response from refresh-campaign-metrics:", response.data);
        throw new Error(response.data.error || 'Failed to refresh metrics');
      }
      
      // Refetch campaigns to get updated metrics
      await queryClient.invalidateQueries({ queryKey: ['twitter-campaigns'] });
      
      toast({
        title: 'Metrics refreshed',
        description: 'Campaign metrics have been updated successfully'
      });
    } catch (err) {
      console.error('Error refreshing metrics:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to refresh campaign metrics',
        variant: 'destructive'
      });
    } finally {
      setRefreshingMetrics(null);
    }
  };
  
  // Create campaign
  const handleCreateCampaign = async () => {
    try {
      // Create the campaign
      const { data: campaign, error: campaignError } = await supabase
        .from('twitter_campaigns')
        .insert({
          name: newCampaignName,
          description: newCampaignDescription || null
        })
        .select()
        .single();
        
      if (campaignError) throw campaignError;
      
      toast({
        title: 'Campaign created',
        description: `Campaign "${newCampaignName}" was created successfully`
      });
      
      // Reset form state
      resetFormState();
      queryClient.invalidateQueries({ queryKey: ['twitter-campaigns'] });
    } catch (err) {
      console.error('Error creating campaign:', err);
      toast({
        title: 'Error',
        description: 'Failed to create campaign',
        variant: 'destructive'
      });
    }
  };
  
  // Update campaign
  const handleUpdateCampaign = async () => {
    if (!currentCampaign) return;
    
    try {
      const { error } = await supabase
        .from('twitter_campaigns')
        .update({
          name: newCampaignName,
          description: newCampaignDescription || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentCampaign.id);
        
      if (error) throw error;
      
      toast({
        title: 'Campaign updated',
        description: `Campaign "${newCampaignName}" was updated successfully`
      });
      
      // Reset form state
      resetFormState();
      queryClient.invalidateQueries({ queryKey: ['twitter-campaigns'] });
    } catch (err) {
      console.error('Error updating campaign:', err);
      toast({
        title: 'Error',
        description: 'Failed to update campaign',
        variant: 'destructive'
      });
    }
  };
  
  // Reset form state after submission or cancel
  const resetFormState = () => {
    setNewCampaignName('');
    setNewCampaignDescription('');
    setIsDialogOpen(false);
    setCurrentCampaign(null);
  };
  
  // Delete campaign
  const handleDeleteCampaign = async (id: string, name: string) => {
    try {
      // First check if campaign has deliverables
      const { count, error: countError } = await supabase
        .from('twitter_deliverables')
        .select('*', { count: 'exact', head: true })
        .eq('campaign_id', id);
        
      if (countError) throw countError;
      
      if (count && count > 0) {
        toast({
          title: 'Cannot delete campaign',
          description: 'This campaign has deliverables. Delete all deliverables first.',
          variant: 'destructive'
        });
        return;
      }
      
      const { error } = await supabase
        .from('twitter_campaigns')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      toast({
        title: 'Campaign deleted',
        description: `Campaign "${name}" was deleted successfully`
      });
      
      queryClient.invalidateQueries({ queryKey: ['twitter-campaigns'] });
    } catch (err) {
      console.error('Error deleting campaign:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete campaign',
        variant: 'destructive'
      });
    }
  };
  
  // Handle edit campaign
  const handleEditCampaign = (campaign: TwitterCampaign) => {
    setCurrentCampaign(campaign);
    setNewCampaignName(campaign.name);
    setNewCampaignDescription(campaign.description || '');
    setDialogMode('edit');
    setIsDialogOpen(true);
  };
  
  // Handle dialog form submission based on mode
  const handleDialogSubmit = () => {
    if (dialogMode === 'add') {
      handleCreateCampaign();
    } else {
      handleUpdateCampaign();
    }
  };
  
  // Handle new campaign dialog
  const handleNewCampaign = () => {
    setCurrentCampaign(null);
    setNewCampaignName('');
    setNewCampaignDescription('');
    setDialogMode('add');
    setIsDialogOpen(true);
  };

  // Format large numbers with K, M suffix
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };
  
  if (error) {
    return (
      <EmptyStateAnalytics message="Error loading Twitter campaigns" />
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Twitter Campaigns</h3>
        <Button variant="outline" size="sm" onClick={handleNewCampaign} className="flex items-center gap-1">
          <PlusCircle className="h-4 w-4" />
          <span>New Campaign</span>
        </Button>
      </div>
      
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className={`animate-pulse ${isLightMode ? 'bg-gray-100' : 'bg-[#1a1a1a]'}`}>
              <CardHeader>
                <div className={`h-5 w-3/4 ${isLightMode ? 'bg-gray-200' : 'bg-[#2a2a2a]'} rounded`}></div>
                <div className={`h-4 w-1/2 ${isLightMode ? 'bg-gray-200' : 'bg-[#2a2a2a]'} rounded`}></div>
              </CardHeader>
              <CardContent>
                <div className={`h-20 ${isLightMode ? 'bg-gray-200' : 'bg-[#2a2a2a]'} rounded`}></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : campaigns.length === 0 ? (
        <EmptyStateAnalytics message="No campaigns found. Create your first Twitter campaign to get started." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map((campaign) => (
            <Card key={campaign.id} className="overflow-hidden">
              <CardHeader className="pb-3 relative">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-base">{campaign.name}</CardTitle>
                    {campaign.description && (
                      <CardDescription className="mt-1 line-clamp-2">{campaign.description}</CardDescription>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditCampaign(campaign)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="text-red-500 cursor-pointer"
                        onClick={() => handleDeleteCampaign(campaign.id, campaign.name)}
                      >
                        <Trash className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                {/* Metrics display with hover animation */}
                <div className="flex flex-wrap gap-2 mt-3">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="bg-muted/50 hover:bg-muted px-2 py-1 rounded-md text-xs flex items-center gap-1 transition-transform hover:-translate-y-1">
                          <span className="font-medium">{formatNumber(campaign.posts_count || 0)}</span>
                          <span className="text-muted-foreground">posts</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>Total posts in this campaign</TooltipContent>
                    </Tooltip>
                    
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="bg-muted/50 hover:bg-muted px-2 py-1 rounded-md text-xs flex items-center gap-1 transition-transform hover:-translate-y-1">
                          <span className="font-medium">{formatNumber(campaign.engagements_count || 0)}</span>
                          <span className="text-muted-foreground">engagements</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>Total engagements (likes, retweets, replies, quotes)</TooltipContent>
                    </Tooltip>
                    
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="bg-muted/50 hover:bg-muted px-2 py-1 rounded-md text-xs flex items-center gap-1 transition-transform hover:-translate-y-1">
                          <span className="font-medium">{formatNumber(campaign.impressions_count || 0)}</span>
                          <span className="text-muted-foreground">impressions</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>Total impressions</TooltipContent>
                    </Tooltip>
                    
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="bg-muted/50 hover:bg-muted px-2 py-1 rounded-md text-xs flex items-center gap-1 transition-transform hover:-translate-y-1">
                          <span className="font-medium">{campaign.engagement_rate?.toFixed(2) || 0}%</span>
                          <span className="text-muted-foreground">engagement rate</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>Engagement rate (engagements/impressions)</TooltipContent>
                    </Tooltip>
                    
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 px-2 rounded-md"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRefreshMetrics(campaign.id);
                          }}
                          disabled={refreshingMetrics === campaign.id}
                        >
                          <RefreshCw className={`h-3 w-3 ${refreshingMetrics === campaign.id ? 'animate-spin' : ''}`} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Refresh metrics</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </CardHeader>
              <CardContent className="pb-3">
                <div className="flex items-center justify-between text-muted-foreground text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>{new Date(campaign.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="border-t pt-3 pb-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => onCampaignSelect(campaign.id)}
                >
                  View Campaign
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
      
      {/* Dialog for adding/editing campaigns */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialogMode === 'add' ? 'Create New Campaign' : 'Edit Campaign'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="campaign-name" className="text-sm font-medium">Campaign Name</label>
              <Input
                id="campaign-name"
                value={newCampaignName}
                onChange={(e) => setNewCampaignName(e.target.value)}
                placeholder="Enter campaign name"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="campaign-description" className="text-sm font-medium">Description (optional)</label>
              <Input
                id="campaign-description"
                value={newCampaignDescription}
                onChange={(e) => setNewCampaignDescription(e.target.value)}
                placeholder="Enter campaign description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetFormState}>Cancel</Button>
            <Button 
              onClick={handleDialogSubmit} 
              disabled={!newCampaignName}
            >
              {dialogMode === 'add' ? 'Create Campaign' : 'Update Campaign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
